import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Hourglass, User, Flag, CalendarClock, Pencil } from "lucide-react";
import CreateJobDialog from "@/components/CreateJobDialog";
import SearchBar from "@/components/SearchBar";
import { Highlight } from "@/lib/highlight";
import { useAuth } from "@/context/AuthContext";

// Jobs that are partly cleaned and awaiting confirmation. Off the schedule
// until a manager books them back in with a date.
export default function WorkInProgressPage() {
    const { user } = useAuth();
    const isManager = user?.role === "manager";
    const [jobs, setJobs] = useState([]);
    const [operatives, setOperatives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);
    const [query, setQuery] = useState("");

    const load = async () => {
        try {
            const [j, u] = await Promise.all([
                api.get("/jobs?scope=wip"),
                isManager ? api.get("/users") : Promise.resolve({ data: [] }),
            ]);
            setJobs(j.data);
            setOperatives(u.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const sorted = [...jobs].sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
        if (!q) return sorted;
        return sorted.filter(j => [j.title, j.notes, j.client_name, j.assigned_to_name, j.address]
            .filter(Boolean).join(" ").toLowerCase().includes(q));
    }, [jobs, query]);

    const onUpdated = (updated) => {
        if (updated && !updated.is_wip) {
            setJobs(prev => prev.filter(j => j.id !== updated.id));
        } else {
            setJobs(prev => prev.map(j => j.id === updated.id ? updated : j));
        }
    };

    return (
        <div className="space-y-6" data-testid="wip-page">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <span className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">Work in progress</span>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Partly cleaned — awaiting confirmation</h1>
                    <p className="text-[var(--text-secondary)] mt-1 text-sm max-w-2xl">
                        Jobs parked here stay off the schedule until they're booked back in with a date.
                        {isManager && " Assigned operatives can still open them to read notes and photos."}
                    </p>
                </div>
                {isManager && (
                    <button data-testid="wip-add-btn" onClick={() => setDialog({ mode: "create" })}
                            className="btn-primary inline-flex items-center gap-2">
                        <Plus size={14} /> Add to Work in progress
                    </button>
                )}
            </div>

            <SearchBar value={query} onChange={setQuery} placeholder="Search Work in progress — client, address, notes…"
                       resultLabel={query.trim() ? `${filtered.length} match${filtered.length === 1 ? "" : "es"}` : null} />

            {loading ? (
                <div className="text-sm text-[var(--text-muted)]">Loading…</div>
            ) : filtered.length === 0 ? (
                <div data-testid="wip-empty" className="card-flat p-10 text-center border-dashed">
                    <Hourglass size={28} className="mx-auto text-[var(--text-muted)]" />
                    <div className="mt-3 font-semibold">{jobs.length === 0 ? "Nothing in progress" : "No matches"}</div>
                    <div className="text-sm text-[var(--text-muted)] mt-1">Partly cleaned jobs awaiting confirmation will show here.</div>
                </div>
            ) : (
                <div className="card-flat divide-y divide-[var(--border-soft)] overflow-hidden" data-testid="wip-list">
                    {filtered.map(job => (
                        <WipRow key={job.id} job={job} query={query} isManager={isManager}
                                 onBookIn={() => setDialog({ mode: "book", job })}
                                 onEdit={() => setDialog({ mode: "edit", job })} />
                    ))}
                </div>
            )}

            {isManager && (
                <CreateJobDialog
                    open={Boolean(dialog)}
                    onClose={() => setDialog(null)}
                    onCreated={() => load()}
                    onUpdated={onUpdated}
                    operatives={operatives}
                    job={dialog?.job || null}
                    wipMode={dialog?.mode === "create"}
                    bookInMode={dialog?.mode === "book"}
                />
            )}
        </div>
    );
}

const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : null;

function WipRow({ job, query, isManager, onBookIn, onEdit }) {
    const navigate = useNavigate();
    const created = fmtDate(job.created_at);
    const lastDate = fmtDate(job.scheduled_at);
    return (
        <div data-testid={`wip-card-${job.id}`}
             className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 sm:px-5 py-4 hover:bg-[var(--bg-subtle)] transition-colors"
             style={{ borderLeft: "4px solid #D9A441" }}>
            <div className="sm:w-36 shrink-0" data-testid={`wip-created-${job.id}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Created</div>
                <div className="text-sm font-semibold">{created || "—"}</div>
            </div>
            <button type="button" onClick={() => navigate(`/jobs/${job.id}`)} className="text-left flex-1 min-w-0"
                    data-testid={`wip-open-${job.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-semibold leading-tight"><Highlight text={job.title} query={query} /></span>
                    {job.priority === "high" && <span className="pill priority-high"><Flag size={12} /> High</span>}
                </div>
                <div className="text-sm text-[var(--text-secondary)] truncate">
                    <Highlight text={job.client_name || "—"} query={query} />
                    {job.address && <span className="text-[var(--text-muted)]"> · <Highlight text={job.address} query={query} /></span>}
                </div>
                <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5 mt-0.5">
                    <User size={12} className="shrink-0" />
                    {job.assigned_to_name || <span className="italic text-[var(--text-muted)]">Not assigned</span>}
                    {lastDate && <span className="text-[var(--text-muted)]">· last booked {lastDate}</span>}
                </div>
                {job.notes && <div className="text-xs text-[var(--text-muted)] line-clamp-1 mt-0.5">{job.notes}</div>}
            </button>
            {isManager && (
                <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`wip-book-in-${job.id}`} onClick={onBookIn}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--pine)] text-white text-xs font-semibold py-1.5 px-3 hover:opacity-90 transition-opacity">
                        <CalendarClock size={12} /> Send back to schedule
                    </button>
                    <button data-testid={`wip-edit-${job.id}`} onClick={onEdit} aria-label="Edit details"
                            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--pine)] hover:bg-[var(--pine-light)]">
                        <Pencil size={13} />
                    </button>
                </div>
            )}
        </div>
    );
}
